/**
 * The labelled Teacher / Assistant lines on a `WorkshopList` card.
 *
 * Replaces the earlier overlapping-avatars-plus-joined-name treatment
 * ("Eden Park + Sasha") with two independent lines, matching the design. The
 * two lines are independently gated — an assistant-only workshop now gets a
 * line at all, which the avatar version never showed because the whole block
 * was gated on the lead.
 */
import type { WorkshopListSize } from './workshopListSizes';
import { WORKSHOP_LIST_SIZES } from './workshopListSizes';

const ELLIPSIS = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/** The "Teacher:" / "Assistant:" words themselves. Quieter than the name
 *  beside them, but still readable — this is the same colour the sub-region
 *  line above uses. */
const LABEL = { fontWeight: 700, color: 'var(--nb-muted)' } as const;

export interface WorkshopListStaffProps {
  teacher?: string | null;
  assistant?: string | null;
  showTeacher: boolean;
  showAssistant: boolean;
  size?: WorkshopListSize;
}

export function WorkshopListStaff({
  teacher,
  assistant,
  showTeacher,
  showAssistant,
  size = 'md',
}: WorkshopListStaffProps) {
  if (!showTeacher && !showAssistant) return null;
  return (
    <span
      style={{
        fontSize: WORKSHOP_LIST_SIZES[size].staff,
        color: 'var(--nb-ink)',
        fontWeight: 600,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {showTeacher && (
        <span style={ELLIPSIS}>
          <span style={LABEL}>Teacher: </span>
          {teacher}
        </span>
      )}
      {showAssistant && (
        <span style={ELLIPSIS}>
          <span style={LABEL}>Assistant: </span>
          {assistant}
        </span>
      )}
    </span>
  );
}
